import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Service for managing user groups
 */
class GroupControlService {
    constructor() {
        this.baseUrl = `${API_BASE_URL}/api/v2/groups`;
    }

    /**
     * Get all user groups
     * @returns {Promise<Array>} List of user groups
     */
    async getAllGroups() {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.get(this.baseUrl, { headers });
            return response.data;
        } catch (error) {
            console.error('Error fetching groups:', error);
            throw error;
        }
    }

    /**
     * Create a new user group
     * @param {Object} groupData - Group data including name and members
     * @returns {Promise<Object>} Created group
     */
    async createGroup(groupData) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.post(this.baseUrl, groupData, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating group:', error);
            throw error;
        }
    }

    /**
     * Update an existing user group
     * @param {string} groupId - ID of the group to update
     * @param {Object} groupData - Updated group data
     * @returns {Promise<Object>} Updated group
     */
    async updateGroup(groupId, groupData) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.put(`${this.baseUrl}/${groupId}`, groupData, { headers });
            return response.data;
        } catch (error) {
            console.error('Error updating group:', error);
            throw error;
        }
    }

    /**
     * Delete a user group
     * @param {string} groupId - ID of the group to delete
     * @returns {Promise<boolean>} Success status
     */
    async deleteGroup(groupId) {
        try {
            const headers = getAuthHeaders(true);
            await axios.delete(`${this.baseUrl}/${groupId}`, { headers });
            return true;
        } catch (error) {
            console.error('Error deleting group:', error);
            throw error;
        }
    }

    /**
     * Add users to a group
     * @param {string} groupId - ID of the group
     * @param {Array<string>} userEmails - List of user emails to add
     * @returns {Promise<Object>} Updated group
     */
    async addUsersToGroup(groupId, userEmails) {
        try {
            const headers = getAuthHeaders(true);
            const response = await axios.post(`${this.baseUrl}/${groupId}/members`, userEmails, { headers });
            return response.data;
        } catch (error) {
            console.error('Error adding users to group:', error);
            throw error;
        }
    }

    /**
     * Remove users from a group
     * @param {string} groupId - ID of the group
     * @param {Array<string>} userEmails - List of user emails to remove
     * @returns {Promise<Object>} Updated group
     */
    async removeUsersFromGroup(groupId, userEmail) {
        try {
            const headers = getAuthHeaders(true);
            const groupData = {
                operation: 'removeUsers',
                users: [userEmail],
            };
            const response = await axios.delete(`${this.baseUrl}/${groupId}/members?userEmail=${userEmail}`, {
                headers,
            });
            return response.data;
        } catch (error) {
            console.error('Error removing users from group:', error);
            throw error;
        }
    }
}

const groupControlService = new GroupControlService();
export default groupControlService;
